import { useState } from 'react';
import { cn } from '@/lib/cn';

const TILES = ['var(--tile-1)', 'var(--tile-2)', 'var(--tile-3)', 'var(--tile-4)', 'var(--tile-5)', 'var(--tile-6)'] as const;

/** Stable colour per app, so a letter tile keeps its colour across screens. */
const tileFor = (seed: string): string => {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return TILES[Math.abs(hash) % TILES.length] ?? TILES[0];
};

const letterFor = (name: string): string => (/[\p{L}\p{N}]/u.exec(name)?.[0] ?? '?').toUpperCase();

type AppLogoProps = {
  name: string;
  /** Store icon URL (App Store or Google Play): the API's `icon_url` for the app. */
  iconUrl?: string | null | undefined;
  /** Stable id for the tile colour; defaults to the name. */
  seed?: string;
  size?: number;
  /**
   * Decorative by default: the app name is printed next to the logo. Set false
   * when the logo stands alone, and it is announced by name.
   */
  decorative?: boolean;
  className?: string;
};

/**
 * App identity is shown with a logo, not a name alone (decision 2026-09-30).
 * Uses the store icon; falls back to a letter tile while none is known or the
 * image fails to load.
 */
export function AppLogo({ name, iconUrl, seed, size = 24, decorative = true, className }: AppLogoProps) {
  const [failed, setFailed] = useState(false);
  const a11y = decorative ? { 'aria-hidden': true as const } : { role: 'img' as const, 'aria-label': name };
  if (iconUrl && !failed) {
    return (
      <img
        // Hidden like the tile when decorative: its title would otherwise
        // name the image and repeat the app name in its control's label.
        {...(decorative ? { 'aria-hidden': true as const } : {})}
        src={iconUrl}
        alt={decorative ? '' : name}
        title={name}
        width={size}
        height={size}
        loading="lazy"
        onError={() => { setFailed(true); }}
        className={cn('block shrink-0 rounded-[22%] object-cover shadow-[0_0_0_1px_var(--line)]', className)}
      />
    );
  }
  return (
    <span
      {...a11y}
      title={name}
      style={{ width: size, height: size, background: tileFor(seed ?? name), fontSize: Math.max(9, Math.round(size * 0.45)) }}
      className={cn('grid shrink-0 place-items-center rounded-[22%] font-bold text-on-tile', className)}
    >
      {letterFor(name)}
    </span>
  );
}
